'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';

import { Dropdown, Image, Skeleton } from 'next-bricks';

import { Icon } from 'components/Icon';
import { routes } from 'config/routes';
import { useAuth } from 'contexts/AuthContext';
import { useAvatar } from 'hooks/useAvatar';

import styles from './styles.module.css';

interface UserAvatarProps {
	size: number;
}

function UserAvatar({ size }: UserAvatarProps) {
	// Hooks
	const auth = useAuth();
	const avatar = useAvatar();
	const pathname = usePathname();

	// Memo vars
	const isDashboard = useMemo(() => {
		return (
			pathname.startsWith(routes.get('dashboard')) ||
			pathname.startsWith(routes.get('admin'))
		);
	}, [pathname]);

	if (!auth.isAuthenticated || auth.isLoading) {
		return (
			<Skeleton
				width={size}
				height={size}
				className={styles.skeleton}
				circle
			/>
		);
	}

	return (
		<Dropdown
			placement="bottom-right"
			disableAutoCloseWhenClosest={['.theme']}
			items={[
				{
					id: 'home',
					content: isDashboard ? 'Página inicial' : 'Dashboard',
					linkTo: isDashboard
						? routes.get('/')
						: auth.permissions?.role === 'admin'
							? routes.get('admin')
							: routes.get('dashboard'),
				},
				{
					id: 'logout',
					content: (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
							}}
						>
							<Icon name="LogOut" />
							Sair da conta
						</div>
					),
					linkTo: routes.get('auth.logout'),
				},
			]}
		>
			<div
				className={styles.container}
				style={{ width: size, height: size }}
			>
				<Image
					src={
						auth.user?.photoURL
							? auth.user?.photoURL
							: auth.user?.displayName
								? avatar.generate(auth.user.displayName)
								: avatar.generate(
										auth.user?.email?.split('@')[0],
									)
					}
					alt={`Foto do usuário ${auth.user?.displayName}`}
					width={size}
					height={size}
				/>
			</div>
		</Dropdown>
	);
}

export default UserAvatar;
